import { Divider } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Step, { StepProps } from "@mui/material/Step";
import StepLabel, { StepLabelProps } from "@mui/material/StepLabel";
import MobileStepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { DialogStep } from "~/components/GenericStepperDialog";
import { CircularLoadingWithLabel } from "../components/CircularLoadingWithLabel";
import { useWPRContext } from "./hooks/useWPRContext";
import { WPRStartStep } from "./WPRStartStep";
import { WPRStepOne } from "./WPRStepOne";
import { WPRStepThree } from "./WPRStepThree";
import { WPRStepTwo } from "./WPRStepTwo";

type CustomStep = { stepProps?: StepProps } & DialogStep & {
    stepLabelProps?: StepLabelProps;
    useDefaultStepHandler?: boolean;
    description?: string;
  };
export type StepChangeClickedType = "next" | "reset" | "back" | "stay";
export type StepChildProps = {
  stepChangeClickedType: StepChangeClickedType;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  setStepChangeClickedType: React.Dispatch<
    React.SetStateAction<StepChangeClickedType>
  >;
};

type WPRStepperProps = {
  setShowStepper: React.Dispatch<React.SetStateAction<boolean>>;
};

export const WPRStepper = ({ setShowStepper }: WPRStepperProps) => {
  const [activeStep, setActiveStep] = React.useState(0);

  const { t } = useTranslation();
  const [stepChangeClickedType, setStepChangeClickedType] =
    React.useState<StepChangeClickedType>("stay");

  const { stepLoadingText, selectedExcelBase } = useWPRContext();
  // Asetuksien määrittämisen jälkeen siirry seuravaan vaiheeseen
  const steps: CustomStep[] = React.useMemo<CustomStep[]>(
    () => [
      {
        title: t("WPR:firstStep"),
        description: t("WPR:firstStepDesc"),
        content: <WPRStartStep />,
        stepLabelProps: { optional: false },
        useDefaultStepHandler: true,
      },
      {
        title: t("WPR:stepOne"),
        description: t("WPR:stepOneDesc"),
        content: (
          <WPRStepOne
            stepChangeClickedType={stepChangeClickedType}
            setActiveStep={setActiveStep}
            setStepChangeClickedType={setStepChangeClickedType}
          />
        ),
        stepLabelProps: { optional: false },
      },
      {
        title: t("WPR:stepTwo"),
        description: t("WPR:stepTwoDesc"),
        content: (
          <WPRStepTwo
            stepChangeClickedType={stepChangeClickedType}
            setActiveStep={setActiveStep}
            setStepChangeClickedType={setStepChangeClickedType}
          />
        ),
        stepLabelProps: { optional: false },
      },
      {
        title: t("WPR:stepThreeTitle"),
        description: t("WPR:stepThreeDesc"),
        content: (
          <WPRStepThree
            stepChangeClickedType={stepChangeClickedType}
            setActiveStep={setActiveStep}
            setStepChangeClickedType={setStepChangeClickedType}
            setShowStepper={setShowStepper}
          />
        ),
        stepLabelProps: { optional: false },
      },
    ],
    [setShowStepper, stepChangeClickedType, t],
  );

  const activeStepObj = React.useMemo(
    () => steps[activeStep],
    [activeStep, steps],
  );

  const handleNext = () => {
    if (activeStepObj.useDefaultStepHandler) setActiveStep((prev) => prev + 1);
    else setStepChangeClickedType("next");
  };

  const handleBack = () => {
    if (activeStep === 0) setShowStepper(false);
    else if (activeStepObj.useDefaultStepHandler)
      setActiveStep((prev) => prev - 1);
    else setStepChangeClickedType("back");
  };

  // const handleReset = () => {
  //   void qc.invalidateQueries([id, "wpr-list"]);
  //   setShowStepper(false);
  //   setActiveStep(0);
  // };

  const buttonIsDisabled = React.useMemo(
    () => !!stepLoadingText || !selectedExcelBase,
    [selectedExcelBase, stepLoadingText],
  );

  return (
    <Box sx={{ width: "100%" }}>
      <MobileStepper activeStep={activeStep}>
        {steps.map((step) => {
          return (
            <Step key={step.title} {...step.stepProps}>
              <StepLabel {...step.stepLabelProps}>{step.title}</StepLabel>
            </Step>
          );
        })}
      </MobileStepper>

      <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
        <Button
          color="warning"
          variant="contained"
          onClick={handleBack}
          sx={{ m: "auto", mr: 1 }}
        >
          {t("general:back")}
        </Button>
        <Box sx={{ marginX: "10px", flex: "1 1 auto" }}>
          <Typography sx={{ textAlign: "center" }}>
            {activeStepObj?.description}
          </Typography>
        </Box>
        <Button
          sx={{ m: "auto" }}
          color="success"
          variant="contained"
          onClick={handleNext}
          disabled={buttonIsDisabled}
        >
          {activeStep === steps.length - 1
            ? t("general:finish")
            : t("general:next")}
        </Button>
      </Box>

      <Divider sx={{ marginY: "8px" }} />
      {stepLoadingText ? (
        <Box
          sx={{
            minHeight: "200px",
            height: "40%",
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
          }}
        >
          <CircularLoadingWithLabel alignText="column" text={stepLoadingText} />
        </Box>
      ) : (
        steps[activeStep]?.content
      )}
    </Box>
  );
};
